import React, { Dispatch, SetStateAction, useState } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import moment from 'moment';
import { translate } from '../../../services/translate';
import MyAppText from '../../MyAppText';
import FilterDatePicker, { CalendarValue } from '../../Filter/components/FilterDateInput';

export interface PresetPeriodRange {
    beginIn: number;
    endIn: number;
}

interface Props {
    preset: string;
    setPreset: (preset: string, range: PresetPeriodRange | null) => void;
    beginDate: CalendarValue;
    setBeginDate: Dispatch<SetStateAction<CalendarValue>>;
    beginTime: string;
    setBeginTime: (value: string) => void;
    endDate: CalendarValue;
    setEndDate: Dispatch<SetStateAction<CalendarValue>>;
    endTime: string;
    setEndTime: (value: string) => void;
    onClear: () => void;
    clearPreset: () => void;
}

type Preset = {
    key: string;
    getRange: () => PresetPeriodRange;
};

const column1: Preset[] = [
    { key: 'last5min', getRange: () => ({ beginIn: moment().subtract(5, 'minutes').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last15min', getRange: () => ({ beginIn: moment().subtract(15, 'minutes').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last30min', getRange: () => ({ beginIn: moment().subtract(30, 'minutes').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last1h', getRange: () => ({ beginIn: moment().subtract(1, 'hour').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last6h', getRange: () => ({ beginIn: moment().subtract(6, 'hours').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last12h', getRange: () => ({ beginIn: moment().subtract(12, 'hours').valueOf(), endIn: moment().valueOf() }) },
    { key: 'last24h', getRange: () => ({ beginIn: moment().subtract(24, 'hours').valueOf(), endIn: moment().valueOf() }) },
];

const column2: Preset[] = [
    { key: 'yesterday', getRange: () => ({ beginIn: moment().subtract(1, 'day').startOf('day').valueOf(), endIn: moment().subtract(1, 'day').endOf('day').valueOf() }) },
    { key: 'today', getRange: () => ({ beginIn: moment().startOf('day').valueOf(), endIn: moment().endOf('day').valueOf() }) },
    { key: 'thisWeek', getRange: () => ({ beginIn: moment().startOf('week').valueOf(), endIn: moment().endOf('week').valueOf() }) },
    { key: 'thisMonth', getRange: () => ({ beginIn: moment().startOf('month').valueOf(), endIn: moment().endOf('month').valueOf() }) },
    { key: 'thisYear', getRange: () => ({ beginIn: moment().startOf('year').valueOf(), endIn: moment().endOf('year').valueOf() }) },
];

const column3: Preset[] = [
    { key: 'lastWeek', getRange: () => ({ beginIn: moment().subtract(1, 'week').startOf('week').valueOf(), endIn: moment().subtract(1, 'week').endOf('week').valueOf() }) },
    { key: 'lastMonth', getRange: () => ({ beginIn: moment().subtract(1, 'month').startOf('month').valueOf(), endIn: moment().subtract(1, 'month').endOf('month').valueOf() }) },
    { key: 'lastBimester', getRange: () => ({ beginIn: moment().subtract(2, 'months').startOf('month').valueOf(), endIn: moment().subtract(1, 'month').endOf('month').valueOf() }) },
    { key: 'lastQuarter', getRange: () => ({ beginIn: moment().subtract(3, 'months').startOf('month').valueOf(), endIn: moment().subtract(1, 'month').endOf('month').valueOf() }) },
    { key: 'lastSemester', getRange: () => ({ beginIn: moment().subtract(6, 'months').startOf('month').valueOf(), endIn: moment().subtract(1, 'month').endOf('month').valueOf() }) },
    { key: 'lastYearFIlter', getRange: () => ({ beginIn: moment().subtract(1, 'year').startOf('year').valueOf(), endIn: moment().subtract(1, 'year').endOf('year').valueOf() }) },
];

export default function FilterPeriodSelector({
    preset,
    setPreset,
    beginDate,
    setBeginDate,
    beginTime,
    setBeginTime,
    endDate,
    setEndDate,
    endTime,
    setEndTime,
    onClear,
    clearPreset,
}: Props) {
    const { styles } = useStyles(styleSheet);
    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const isEmpty = preset === '' && !beginDate && !endDate;

    const handleSelectPreset = (item: Preset) => {
        if (preset === item.key) {
            setPreset('', null);
        } else {
            setPreset(item.key, item.getRange());
        }
    };

    const renderItem = (item: Preset) => {
        const isSelected = preset === item.key;
        return (
            <TouchableOpacity
                key={item.key}
                style={styles.item}
                onPress={() => handleSelectPreset(item)}
            >
                <MyAppText style={[styles.itemText, isSelected && styles.itemTextSelected]}>
                    {translate(item.key)}
                </MyAppText>
            </TouchableOpacity>
        );
    };

    return (
        <View style={styles.container}>
            <View style={styles.clearRow}>
                <TouchableOpacity onPress={onClear} disabled={isEmpty}>
                    <MyAppText style={[styles.clearSelection, isEmpty && styles.clearSelectionDisabled]}>
                        {translate('clearSelection')}
                    </MyAppText>
                </TouchableOpacity>
            </View>

            <View style={styles.columns}>
                <View style={styles.column}>{column1.map(renderItem)}</View>
                <View style={styles.column}>{column2.map(renderItem)}</View>
                <View style={styles.column}>{column3.map(renderItem)}</View>
            </View>

            <View style={styles.customContainer}>
                <View style={{ zIndex: estimatedStartZIndex }}>
                    <FilterDatePicker
                        date={beginDate}
                        onChangeDate={(value) => {
                            clearPreset();
                            setBeginDate(value);
                        }}
                        time={beginTime}
                        onChangeTime={(value) => {
                            setPreset('', null);
                            setBeginTime(value);
                        }}
                        label={translate('createdAt')}
                        onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                        onCalendarClose={() => setEstimatedStartZIndex(2)}
                    />
                </View>
                <View style={{ zIndex: estimatedFinishZIndex }}>
                    <FilterDatePicker
                        date={endDate}
                        onChangeDate={(value) => {
                            clearPreset();
                            setEndDate(value);
                        }}
                        time={endTime}
                        onChangeTime={(value) => {
                            setPreset('', null);
                            setEndTime(value);
                        }}
                        label={translate('to')}
                        onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                        onCalendarClose={() => setEstimatedFinishZIndex(1)}
                    />
                </View>
            </View>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        gap: 16,
    },
    clearRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    clearSelectionDisabled: {
        opacity: 0.4,
    },
    columns: {
        flexDirection: 'row',
    },
    column: {
        flex: 1,
        gap: 8,
    },
    item: {},
    itemText: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    itemTextSelected: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        textDecorationLine: 'underline',
    },
    customContainer: {
        gap: 12,
    },
}));
